import React from 'react';
import LegalPageLayout from '../components/LegalPageLayout.jsx';

const h2Style = {
  fontSize: 19,
  fontWeight: 600,
  marginTop: 28,
  marginBottom: 10,
};
const pStyle = { marginBottom: 14 };
const ulStyle = { marginBottom: 14, paddingLeft: 24 };
const liStyle = { marginBottom: 6 };

export default function Terms() {
  return (
    <LegalPageLayout title="Terms of Service — Rooted" lastUpdated="May 13, 2026">
      <h2 style={h2Style}>1. Acceptance of Terms</h2>
      <p style={pStyle}>
        By accessing or using Rooted (the &ldquo;Service&rdquo;), provided by Golden Fox
        Publishing LLC (&ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;our&rdquo;), you agree
        to be bound by these Terms of Service (&ldquo;Terms&rdquo;). If you do not agree
        to these Terms, you may not use the Service.
      </p>

      <h2 style={h2Style}>2. Description of Service</h2>
      <p style={pStyle}>
        Rooted is a faith-based digital Bible study application that uses
        artificial intelligence to generate personalized study content based on
        user input. The Service includes intake conversations, personality
        assessments, daily study materials, reflection prompts, and check-in
        interactions delivered through web and mobile interfaces.
      </p>

      <h2 style={h2Style}>3. Eligibility</h2>
      <p style={pStyle}>
        You must be at least eighteen (18) years of age to use the Service. By
        using the Service, you represent and warrant that you are at least
        eighteen (18) years old.
      </p>

      <h2 style={h2Style}>4. Account Registration</h2>
      <p style={pStyle}>
        To access the Service, you must create an account through our
        authentication provider. You agree to provide accurate, current, and
        complete information during registration and to update such information
        as necessary. You are responsible for maintaining the confidentiality of
        your account credentials and for all activities that occur under your
        account. Notify us immediately at{' '}
        <a href="mailto:mike@goldenfoxrooted.com">mike@goldenfoxrooted.com</a> of
        any unauthorized use of your account.
      </p>

      <h2 style={h2Style}>5. Subscription and Billing</h2>
      <p style={pStyle}>
        The Service is offered on a paid subscription basis at five dollars and
        ninety-nine cents ($5.99) per week, billed weekly through our payment
        processor (Stripe). By subscribing, you authorize us and Stripe to
        charge your payment method on a recurring weekly basis until you cancel.
        Subscription fees are non-refundable except as expressly stated in these
        Terms or as required by law.
      </p>

      <h2 style={h2Style}>6. Cancellation and Refunds</h2>
      <p style={pStyle}>
        You may cancel your subscription at any time through the Stripe Customer
        Portal accessible from your profile or by emailing{' '}
        <a href="mailto:mike@goldenfoxrooted.com">mike@goldenfoxrooted.com</a>.
        Cancellation takes effect at the end of your current billing period; you
        will retain access through that period. We do not offer prorated refunds
        for partial weeks. The Company may, at its sole discretion, issue
        refunds on a case-by-case basis.
      </p>

      <h2 style={h2Style}>7. Acceptable Use</h2>
      <p style={pStyle}>
        You agree not to: (a) use the Service for any unlawful purpose; (b)
        attempt to gain unauthorized access to any portion of the Service; (c)
        reverse engineer, decompile, or disassemble any portion of the Service;
        (d) interfere with or disrupt the Service; (e) use automated means
        (bots, scrapers, etc.) to access the Service; (f) share your account
        credentials with others; (g) use the Service to harass, abuse, or harm
        another person; or (h) submit content that is illegal, obscene,
        defamatory, or infringes the rights of others.
      </p>

      <h2 style={h2Style}>8. User Content</h2>
      <p style={pStyle}>
        You retain ownership of all content you submit to the Service, including
        intake responses, voice recordings, reflections, and check-in responses
        (&ldquo;User Content&rdquo;). By submitting User Content, you grant the Company a
        non-exclusive, worldwide, royalty-free license to use, store, reproduce,
        and process such content as necessary to provide the Service to you. We
        may use anonymized, aggregated data derived from User Content to improve
        the Service.
      </p>

      <h2 style={h2Style}>9. AI-Generated Content</h2>
      <p style={pStyle}>
        The Service uses artificial intelligence — including large language
        models provided by Anthropic, voice synthesis by ElevenLabs, and speech
        transcription by OpenAI — to generate study content (&ldquo;AI Content&rdquo;).
        You acknowledge and agree that:
      </p>
      <ul style={ulStyle}>
        <li style={liStyle}>
          AI Content is generated algorithmically and may contain inaccuracies,
          errors, theological imprecision, or content unsuitable for your
          specific circumstances
        </li>
        <li style={liStyle}>
          AI Content does not constitute professional, theological, medical,
          legal, psychological, or pastoral advice
        </li>
        <li style={liStyle}>
          We do not guarantee the accuracy, completeness, or reliability of AI
          Content
        </li>
        <li style={liStyle}>
          You should not make significant life, spiritual, medical, financial,
          or legal decisions based solely on AI Content
        </li>
      </ul>

      <h2 style={h2Style}>10. Not Professional Advice; Crisis Resources</h2>
      <p style={pStyle}>
        The Service is intended for general spiritual encouragement and personal
        reflection.{' '}
        <strong>
          It is NOT a substitute for professional counseling, mental health
          treatment, pastoral care, medical advice, legal counsel, or any other
          form of professional service.
        </strong>
      </p>
      <p style={pStyle}>
        If you are experiencing a mental health crisis, suicidal thoughts, or
        are in immediate danger, please contact emergency services or one of the
        following resources immediately:
      </p>
      <ul style={ulStyle}>
        <li style={liStyle}>
          <strong>988 Suicide and Crisis Lifeline:</strong> dial or text{' '}
          <strong>988</strong>
        </li>
        <li style={liStyle}>
          <strong>Crisis Text Line:</strong> text <strong>HOME</strong> to{' '}
          <strong>741741</strong>
        </li>
        <li style={liStyle}>
          <strong>National Domestic Violence Hotline:</strong>{' '}
          <strong>1-800-799-7233</strong>
        </li>
        <li style={liStyle}>
          <strong>In a life-threatening emergency, call 911</strong>
        </li>
      </ul>

      <h2 style={h2Style}>11. Intellectual Property</h2>
      <p style={pStyle}>
        The Service — including its design, code, content, trademarks, and
        features — is the property of Golden Fox Publishing LLC and is protected
        by United States and international intellectual property laws. You may
        not copy, modify, distribute, sell, or lease any part of the Service or
        its content without our prior written consent.
      </p>

      <h2 style={h2Style}>12. Third-Party Services</h2>
      <p style={pStyle}>
        The Service relies on third-party providers including Anthropic (AI
        processing), OpenAI (speech transcription), ElevenLabs (voice
        synthesis), Clerk (authentication), Stripe (payment processing),
        Supabase (data storage), Vercel (hosting), and Sentry (error
        monitoring). Your use of the Service is subject to these third parties&apos;
        respective terms and privacy policies. We are not responsible for the
        actions, content, or policies of these third parties.
      </p>

      <h2 style={h2Style}>13. Disclaimer of Warranties</h2>
      <p style={pStyle}>
        THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT
        WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT
        LIMITATION IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
        PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ACCURACY. WE DO NOT WARRANT
        THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES
        OR OTHER HARMFUL COMPONENTS.
      </p>

      <h2 style={h2Style}>14. Limitation of Liability</h2>
      <p style={pStyle}>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, GOLDEN FOX PUBLISHING
        LLC, ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE
        FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
        DAMAGES, INCLUDING WITHOUT LIMITATION LOSS OF PROFITS, DATA, USE,
        GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO OR
        USE OF OR INABILITY TO ACCESS OR USE THE SERVICE.
      </p>
      <p style={pStyle}>
        OUR TOTAL CUMULATIVE LIABILITY ARISING OUT OF OR RELATING TO THESE TERMS
        OR THE SERVICE SHALL NOT EXCEED THE GREATER OF: (A) THE AMOUNT YOU PAID
        FOR THE SERVICE IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B)
        ONE HUNDRED DOLLARS ($100.00 USD).
      </p>

      <h2 style={h2Style}>15. Indemnification</h2>
      <p style={pStyle}>
        You agree to indemnify, defend, and hold harmless Golden Fox Publishing
        LLC and its officers, directors, employees, and agents from any claims,
        damages, losses, liabilities, costs, and expenses (including reasonable
        attorneys&apos; fees) arising out of or relating to: (a) your use of the
        Service; (b) your User Content; (c) your violation of these Terms; or
        (d) your violation of any rights of a third party.
      </p>

      <h2 style={h2Style}>16. Termination</h2>
      <p style={pStyle}>
        We may suspend or terminate your access to the Service at any time, with
        or without cause, with or without notice. Upon termination, your right
        to use the Service ceases immediately. Sections that by their nature
        should survive termination (including intellectual property,
        disclaimers, limitations of liability, indemnification, and governing
        law) shall survive.
      </p>

      <h2 style={h2Style}>17. Governing Law and Dispute Resolution</h2>
      <p style={pStyle}>
        These Terms are governed by the laws of the State of Indiana, United
        States, without regard to its conflict-of-laws principles. Any dispute
        arising out of or relating to these Terms or the Service shall be
        resolved in the state or federal courts located in Hamilton County,
        Indiana, and you consent to the personal jurisdiction of such courts.
      </p>

      <h2 style={h2Style}>18. Changes to These Terms</h2>
      <p style={pStyle}>
        We may modify these Terms at any time. We will provide notice of
        material changes by email or through the Service. Your continued use of
        the Service after such notice constitutes acceptance of the modified
        Terms.
      </p>

      <h2 style={h2Style}>19. Severability</h2>
      <p style={pStyle}>
        If any provision of these Terms is found to be unenforceable, the
        remaining provisions shall remain in full force and effect.
      </p>

      <h2 style={h2Style}>20. Entire Agreement</h2>
      <p style={pStyle}>
        These Terms constitute the entire agreement between you and Golden Fox
        Publishing LLC regarding the Service.
      </p>

      <h2 style={h2Style}>21. Contact</h2>
      <p style={pStyle}>Questions about these Terms? Contact us at:</p>
      <p style={pStyle}>
        Golden Fox Publishing LLC
        <br />
        <a href="mailto:mike@goldenfoxrooted.com">mike@goldenfoxrooted.com</a>
        <br />
        Fishers, Indiana, USA
      </p>
    </LegalPageLayout>
  );
}
